import classnames from "classnames";
import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import AnimateHeight from "react-animate-height";

import DropdownIcon from "./icons/DropdownIcon";
import SimpleDropdownItem from "./SimpleDropdownItem";

export type SimpleDropdownOption = {
  href?: string;
  label: JSX.Element;
  onSelect?: (value: string) => void;
  separator?: boolean;
  value: string;
};

export type SimpleDropdownProps = {
  icon?: JSX.Element;
  label: string | JSX.Element;
  onMainClick?: {
    href?: string;
    onClick?: () => void;
  };
  options: SimpleDropdownOption[];
  style: "blue" | "black" | "white";
  className?: string;
};

/**
 * This is a simple dropdown.
 * If you need search, option creation, etc, look at Dropdown instead.
 */
const SimpleDropdown: FunctionComponent<SimpleDropdownProps> = ({
  icon,
  label,
  style,
  onMainClick,
  options,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const outerNode = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (outerNode.current) {
        if (!outerNode.current.contains(e.target as Node)) {
          close();
        }
      }
    };

    document.addEventListener("mousedown", handleClick, false);
    return () => document.removeEventListener("mousedown", handleClick, false);
  }, []);

  const onOpenClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onMainClick) {
      if (onMainClick.onClick) {
        onMainClick.onClick();
      }
      if (onMainClick.href) {
        window.open(onMainClick.href);
      }
    } else {
      setOpen(!open);
    }
  };

  const classes = classnames(className, "simple-dropdown", style, {
    open,
    onMainClick,
  });

  return (
    <div className={classes} ref={outerNode}>
      <button className="simple-dropdown-button">
        <div className="main-button" onClick={onOpenClick}>
          {icon && <div className="icon">{icon}</div>}
          <span>{label}</span>
        </div>
        <div className="dropdown-icon" onClick={() => setOpen(!open)}>
          <DropdownIcon />
        </div>
      </button>
      <AnimateHeight
        style={{ position: "absolute", width: "100%", zIndex: 4 }}
        height={open ? "auto" : 0}
        duration={100}
      >
        <div className="simple-dropdown-menu">
          <div className="simple-dropdown-links">
            {options.map((option, i) => (
              <SimpleDropdownItem option={option} close={close} key={i} />
            ))}
          </div>
        </div>
      </AnimateHeight>
    </div>
  );
};

export default SimpleDropdown;
